/**
 * Horas extra del equipo contra su tope anual.
 *
 * El tope no es igual para todos: es min(convenio, 80) proporcional a la
 * jornada (art. 35.2 ET). Media jornada → 40 h. Tiempo parcial → prohibidas
 * (art. 12.4.c): aquí sale como "prohibidas", no como "0 de 0".
 */
import { useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { getTeamOvertime, TeamOvertimeRow } from '../../services/apiClient';

export function OvertimeAdmin() {
  const year = new Date().getFullYear();
  const [rows, setRows] = useState<TeamOvertimeRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getTeamOvertime(year)
      .then(setRows)
      .catch(() => setError('No se pudieron cargar las horas extra'));
  }, [year]);

/**
 * El backend manda `cap_hours: -1` como centinela de "sin tope codificado"
 * (jurisdicción sin techo legal Y convenio sin `max_overtime_annual`), con un
 * comentario que promete que "la UI muestra sin tope". La UI no lo comprobaba
 * en ningún sitio: la fila imprimía "-1 h" de tope y, como `at_cap` sale de
 * `accrued >= cap`, con `-1 >= -1` daba TRUE con cero horas acumuladas — toda
 * la plantilla de un tenant andorrano o de base europea aparecía "en el tope ·
 * bloqueado" en la pantalla que existe justamente para vigilar ese techo.
 */
function sinTope(cap: number | null | undefined): boolean {
  return cap == null || cap < 0;
}

  const barColor = (r: TeamOvertimeRow) =>
    sinTope(r.cap_hours) ? 'var(--ink-3)' : r.at_cap ? 'var(--signal)' : r.percent >= 75 ? 'var(--warn)' : 'var(--brand)';

  return (
    <AdminLayout title="Horas extra y complementarias" subtitle={`Año ${year}`}>
      <div className="max-w-[960px] space-y-4">
        {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>}

        <div className="rounded-2xl bg-surface border border-line overflow-x-auto">
          <div className="grid grid-cols-[1.4fr_1fr_1.2fr_1.2fr] gap-3 px-5 py-3 bg-surface-2 font-mono text-[11px] uppercase text-ink-3 min-w-[560px]">
            <span>Trabajador</span>
            <span>Acumuladas</span>
            <span>Tope anual</span>
            <span>Estado</span>
          </div>
          {rows.map((r) => (
            <div
              key={r.user_id}
              className="grid grid-cols-[1.4fr_1fr_1.2fr_1.2fr] gap-3 px-5 py-3.5 border-t border-line items-center min-w-[560px]"
            >
              <span className="font-semibold text-[14.5px] truncate">{r.name}</span>
              <span className="font-mono text-[14px]">
                {r.forbidden ? '—' : `${r.accrued_hours} h`}
              </span>
              <div className="min-w-[90px]">
                {r.forbidden ? (
                  <span className="font-mono text-[12px] text-ink-3">sin tope: prohibidas</span>
                ) : (
                  <div className="flex items-center gap-2.5">
                    <div className="flex-1 h-[7px] rounded-full bg-surface-3 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.min(r.percent, 100)}%`, background: barColor(r) }}
                      />
                    </div>
                    <span className="font-mono text-[12px] text-ink-2 shrink-0">
                      {sinTope(r.cap_hours) ? 'sin tope' : `${r.cap_hours} h`}
                    </span>
                  </div>
                )}
              </div>
              <span
                className="font-mono text-[12px]"
                style={{
                  color: r.forbidden || sinTope(r.cap_hours)
                    ? 'var(--ink-3)'
                    : r.at_cap
                      ? 'var(--signal)'
                      : r.percent >= 75
                        ? 'var(--warn)'
                        : 'var(--ink-2)',
                }}
              >
                {r.forbidden
                  ? 'tiempo parcial · art. 12.4.c'
                  : sinTope(r.cap_hours)
                    ? 'sin tope legal ni de convenio'
                    : r.at_cap
                      ? 'en el tope · bloqueado'
                      : `${Math.round(r.percent)} % del tope`}
              </span>
            </div>
          ))}
          {rows.length === 0 && !error && (
            <p className="text-center text-[14px] text-ink-3 py-10">Sin horas extra este año ✓</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <div className="card-surface p-5">
            <p className="font-semibold text-[15px] mb-1.5">Horas complementarias · tiempo parcial</p>
            <p className="text-[13.5px] text-ink-2 mb-1">
              Según el pacto firmado, con su tope proporcional. Sin resumen mensual entregado, el
              contrato se presume a jornada completa.
            </p>
            <p className="font-mono text-[11.5px] text-ink-3">art. 12.5 y 12.4.c ET</p>
          </div>
          <div className="card-surface p-5">
            <p className="font-semibold text-[15px] mb-1.5">Vencimientos</p>
            <p className="text-[13.5px] text-ink-2 mb-1">
              Una hora extra se compensa dentro del plazo que fija tu
              jurisdicción y tu convenio — el aplicable lo muestra la pantalla
              de Convenio — o se consolida para pago. El cron nocturno
              consolida las vencidas; el resumen mensual acompaña a la nómina.
            </p>
            <p className="font-mono text-[11.5px] text-ink-3">compensación de horas extra · resumen mensual</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
